import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Menu,
  MenuItem,
  ListSubheader,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useClientScope } from "../../../../../shared/contexts";
import { useClientBranding } from "../../../../../shared/hooks";
import {
  useReport,
  useUpdateReport,
  useNarrateReport,
  useExportPdf,
  useExportPptx,
  useGenerateNarrative,
  useGenerateActionPlan,
} from "../hooks/useReporting";
import { reportingApi } from "../api/reporting";
import { ReportCanvas } from "../components/ReportCanvas";
import { LogoUploadButton } from "../components/LogoUploadButton";
import { PresentationMode } from "../components/PresentationMode";
import { ROUTES } from "../../../../../shared/utils";
import type {
  ReportBlock,
  ChartBlock,
  HeadingBlock,
  NarrativeBlock,
  PageBreakBlock,
  ReportFilters,
} from "../api/types";

// Lightweight unique id for new blocks (crypto.randomUUID is available in all
// supported browsers; falls back to a timestamp+counter if absent).
let blockCounter = 0;
const newId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  blockCounter += 1;
  return `block-${Date.now()}-${blockCounter}`;
};

const SUMMARY_METRICS = [
  "sleep_performance",
  "sleep_duration",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];
const BREAKDOWN_METRICS = [
  "sleep_score",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];

/**
 * Build a fresh block. Data blocks are seeded with the report's current filters
 * (period/audience) so a block added by hand behaves like a generated one; when
 * no filters are known (blank report) they fall back to the client scope only.
 */
const makeBlock = (
  type: ReportBlock["type"],
  filters: ReportFilters,
): ReportBlock => {
  const id = newId();
  switch (type) {
    case "chart":
      return {
        id,
        type: "chart",
        title: "",
        chartKind: "line",
        metric: "recovery_score",
        granularity: "daily",
        windowDays: 90,
      } as ChartBlock;
    case "heading":
      return { id, type: "heading", text: "" } as HeadingBlock;
    case "narrative":
      return { id, type: "narrative", text: "" } as NarrativeBlock;
    case "pageBreak":
      return { id, type: "pageBreak" } as PageBreakBlock;
    case "cover":
      return { id, type: "cover", title: "Section title", subtitle: "" };
    case "execSummary":
      return {
        id,
        type: "execSummary",
        title: "Executive Summary",
        metrics: SUMMARY_METRICS,
        filters,
      };
    case "breakdown":
      return {
        id,
        type: "breakdown",
        title: "Breakdown",
        dimension: "gender",
        metrics: BREAKDOWN_METRICS,
        filters,
      };
    case "kpiCards":
      return {
        id,
        type: "kpiCards",
        title: "Key Metrics",
        metrics: BREAKDOWN_METRICS,
        filters,
      };
    case "aiPanel":
      return {
        id,
        type: "aiPanel",
        title: "AI-Powered Wellness",
        filters,
        show: {
          score: true,
          highlights: true,
          recommendations: true,
          areasToWatch: true,
        },
      };
    case "actionPlan":
      return { id, type: "actionPlan", title: "Action Plan", items: [] };
    case "correlation":
      return {
        id,
        type: "correlation",
        title: "Metric Correlations",
        metrics: BREAKDOWN_METRICS,
        dimension: "department",
        filters,
      };
    case "scatter":
      return {
        id,
        type: "scatter",
        title: "Sleep vs Recovery",
        xMetric: "sleep_score",
        yMetric: "recovery_score",
        dimension: "department",
        filters,
      };
    case "weatherImpact":
      return {
        id,
        type: "weatherImpact",
        title: "Weather Impact",
        metric: "recovery_score",
        filters,
      };
    default:
      return { id, type: "narrative", text: "" } as NarrativeBlock;
  }
};

export const ReportEditor: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { effectiveClientId } = useClientScope();

  const { data: report, isLoading, isError } = useReport(reportId);
  // Render blocks against the client the report is scoped to (falls back to the
  // live scope for legacy/blank reports without a client_id).
  const reportClientId = report?.client_id ?? effectiveClientId;
  const { data: brandingData } = useClientBranding(reportClientId);
  const clientLogoUrl =
    brandingData?.data?.dark_logo_url ||
    brandingData?.data?.logo_dark_url ||
    brandingData?.data?.logo_url ||
    null;
  const updateReport = useUpdateReport(reportId as string);
  const narrate = useNarrateReport(reportId as string);
  const exportPdf = useExportPdf(reportId as string);
  const exportPptx = useExportPptx(reportId as string);
  const generateNarrative = useGenerateNarrative(reportId as string);
  const generateActionPlan = useGenerateActionPlan(reportId as string);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [dirty, setDirty] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [presenting, setPresenting] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [segments, setSegments] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // Seed local state once the report loads.
  useEffect(() => {
    if (report) {
      setTitle(report.title);
      setDescription(report.description ?? "");
      setBlocks(report.layout?.blocks ?? []);
      setDirty(false);
    }
  }, [report]);

  // If narration already exists, fetch a fresh presigned URL for playback.
  useEffect(() => {
    if (report?.audio_status === "ready" && reportId) {
      reportingApi
        .getAudio(reportId)
        .then((r) => {
          setAudioUrl(r.audio_url);
          setSegments(r.segments ?? []);
        })
        .catch(() => {});
    }
  }, [report?.audio_status, reportId]);

  const mutate = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setDirty(true);
  };

  // Inherit period/audience from an existing data block so hand-added blocks
  // match the rest of the report; empty for a truly blank report.
  const inheritedFilters = (): ReportFilters => {
    const withFilters = blocks.find(
      (b) => "filters" in b && b.filters,
    ) as { filters?: ReportFilters } | undefined;
    return withFilters?.filters ?? {};
  };

  const handleAdd = (type: ReportBlock["type"]) => {
    setBlocks((prev) => [...prev, makeBlock(type, inheritedFilters())]);
    setDirty(true);
    setAddMenuAnchor(null);
  };

  const handleSave = async () => {
    await updateReport.mutateAsync({
      title: title.trim() || "Untitled report",
      description,
      layout: { blocks },
    });
    setDirty(false);
    setToast("Report saved");
  };

  // AI narrative generation reads the report's saved layout server-side, so we
  // persist the current edits first, then ask for Director-targeted copy.
  const handleGenerateNarrative = async (): Promise<string> => {
    if (dirty) await handleSave();
    const result = await generateNarrative.mutateAsync(undefined);
    return result.text;
  };

  // Action-plan generation also reads the saved layout server-side, so persist
  // first, then return the generated items for the block to store.
  const handleGenerateActionPlan = async (): Promise<string[]> => {
    if (dirty) await handleSave();
    const result = await generateActionPlan.mutateAsync(undefined);
    return result.items;
  };

  const handleNarrate = async () => {
    // Persist first so narration reflects the current layout.
    if (dirty) await handleSave();
    const result = await narrate.mutateAsync();
    setAudioUrl(result.audio_url);
    setSegments(result.segments ?? []);
    setToast(
      result.mocked
        ? "Narration generated (mock voice — set ELEVENLABS_API_KEY for real audio)"
        : "Narration generated",
    );
  };

  const handleExportPdf = async () => {
    if (dirty) await handleSave();
    const result = await exportPdf.mutateAsync();
    window.open(result.pdf_url, "_blank", "noopener");
    setToast("PDF pack ready");
  };

  const handleExportPptx = async () => {
    if (dirty) await handleSave();
    const result = await exportPptx.mutateAsync();
    window.open(result.pptx_url, "_blank", "noopener");
    setToast("PowerPoint ready");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Couldn't load this report.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTES.REPORTING)}
          size="small"
          color="inherit"
        >
          All reports
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<SlideshowIcon />}
            onClick={() => setPresenting(true)}
            disabled={blocks.length === 0}
          >
            Present
          </Button>
          <Button
            variant="outlined"
            startIcon={
              narrate.isPending ? <CircularProgress size={16} /> : <RecordVoiceOverIcon />
            }
            onClick={handleNarrate}
            disabled={narrate.isPending || blocks.length === 0}
          >
            Narrate
          </Button>
          <Button
            variant="outlined"
            startIcon={
              exportPdf.isPending ? <CircularProgress size={16} /> : <PictureAsPdfIcon />
            }
            onClick={handleExportPdf}
            disabled={exportPdf.isPending || blocks.length === 0}
          >
            PDF pack
          </Button>
          <Button
            variant="outlined"
            startIcon={
              exportPptx.isPending ? <CircularProgress size={16} /> : <SlideshowIcon />
            }
            onClick={handleExportPptx}
            disabled={exportPptx.isPending || blocks.length === 0}
          >
            PowerPoint
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={updateReport.isPending || !dirty}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      <TextField
        fullWidth
        variant="standard"
        placeholder="Report title"
        value={title}
        onChange={(e) => mutate(setTitle)(e.target.value)}
        slotProps={{ input: { style: { fontSize: 30, fontWeight: 700 } } }}
        sx={{ mb: 1 }}
      />
      <TextField
        fullWidth
        variant="standard"
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => mutate(setDescription)(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box
        sx={{
          mb: 3,
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Slide branding — PEAK4 mark shown on every slide; client logo (top-right) is
          used by default when set against the client.
        </Typography>
        <LogoUploadButton clientId={reportClientId} currentLogoUrl={clientLogoUrl} />
      </Box>

      {narrate.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Narration failed. Try again.
        </Alert>
      )}
      {exportPdf.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          PDF export failed. Try again.
        </Alert>
      )}

      {blocks.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Empty report — add a cover, chart, table, or AI panel to get started.
        </Alert>
      ) : (
        <ReportCanvas
          blocks={blocks}
          clientId={reportClientId}
          onChange={mutate(setBlocks)}
          onGenerateNarrative={handleGenerateNarrative}
          onGenerateActionPlan={handleGenerateActionPlan}
        />
      )}

      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={(e) => setAddMenuAnchor(e.currentTarget)}
        sx={{ mt: 1 }}
      >
        Add block
      </Button>
      <Menu
        anchorEl={addMenuAnchor}
        open={!!addMenuAnchor}
        onClose={() => setAddMenuAnchor(null)}
      >
        <ListSubheader>Layout</ListSubheader>
        <MenuItem onClick={() => handleAdd("cover")}>Cover / section title</MenuItem>
        <MenuItem onClick={() => handleAdd("heading")}>Heading</MenuItem>
        <MenuItem onClick={() => handleAdd("narrative")}>Narrative text</MenuItem>
        <MenuItem onClick={() => handleAdd("pageBreak")}>Page break</MenuItem>
        <Divider />
        <ListSubheader>Charts & data</ListSubheader>
        <MenuItem onClick={() => handleAdd("chart")}>Chart</MenuItem>
        <MenuItem onClick={() => handleAdd("kpiCards")}>KPI cards</MenuItem>
        <MenuItem onClick={() => handleAdd("execSummary")}>Executive summary table</MenuItem>
        <MenuItem onClick={() => handleAdd("breakdown")}>Breakdown table</MenuItem>
        <MenuItem onClick={() => handleAdd("correlation")}>Correlation matrix</MenuItem>
        <MenuItem onClick={() => handleAdd("scatter")}>Scatter</MenuItem>
        <MenuItem onClick={() => handleAdd("weatherImpact")}>Weather impact</MenuItem>
        <Divider />
        <ListSubheader>AI</ListSubheader>
        <MenuItem onClick={() => handleAdd("aiPanel")}>AI insights panel</MenuItem>
        <MenuItem onClick={() => handleAdd("actionPlan")}>Action plan</MenuItem>
      </Menu>

      {dirty && (
        <Chip
          label="Unsaved changes"
          color="warning"
          size="small"
          sx={{ position: "fixed", bottom: 16, right: 16 }}
        />
      )}

      <PresentationMode
        open={presenting}
        onClose={() => setPresenting(false)}
        blocks={blocks}
        clientId={reportClientId}
        audioUrl={audioUrl}
        segments={segments}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};
